import React, {
  useState, useEffect, useCallback, Dispatch, SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { Other } from 'simplydo/interfaces';
import util from 'utils/utils';
import toast from 'react-hot-toast';
import api from 'api';

import DiscussionFeed from 'components/lib/Discussion';

type IProjectComments = {
  forIdea: string,
  forId: string,
  onIdeaActivity?: (ideaId: string) => void,
  commentText: string,
  replyText: string,
  setCommentText: Dispatch<SetStateAction<string>>,
  setReplyText: Dispatch<SetStateAction<{ [commentId: string]: { text: string, media: unknown[] } }>>,
}

const ProjectComments = ({
  forIdea,
  forId,
  onIdeaActivity = () => { },
  commentText,
  replyText,
  setCommentText,
  setReplyText,
}: IProjectComments) => {
  const user = useAppSelector<Other.IUserMe>((state) => state.user);
  const { t } = useTranslation();

  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const [commentLoading, setCommentLoading] = useState(false);

  const [commentsState, setCommentsState] = useState({
    page: 1, limit: 15, nextPageAvailable: false, previousPageAvailable: false, total: 0,
  });

  const {
    page, limit, nextPageAvailable, previousPageAvailable, total,
  } = commentsState;

  const getComments = useCallback(() => {
    setCommentsLoading(true);
    api.boards.getProjectComments(forId, forIdea, { page, limit }, (data) => {
      setComments(data.comments);
      setCommentsState((prevState) => ({
        ...prevState, nextPageAvailable: data.nextPageAvailable, previousPageAvailable: data.previousPageAvailable, total: data.total,
      }));
      setCommentsLoading(false);
    }, (err) => {
      toast.error(err.message);
      setCommentsLoading(false);
    });
  }, [forId, forIdea, page, limit, setComments, setCommentsState, setCommentsLoading]);

  useEffect(() => {
    getComments();
  }, [getComments]);

  const onPageChange = useCallback((newPage: number) => {
    setCommentsState((prevState) => ({ ...prevState, page: newPage }));
  }, [setCommentsState]);

  const postComment = useCallback((parent: string | undefined = '') => {
    setCommentLoading(true);
    api.boards.postProjectComment(forId, forIdea, { message: parent ? replyText[parent]?.text ?? '' : commentText, parent }, () => {
      toast.success(t('ideas.comments.posted'));
      if (parent) {
        setReplyText((prev) => {
          const newState = { ...prev };
          delete newState[parent];
          return newState;
        });
        setCommentLoading(false);
        getComments();
      } else {
        setCommentText('');
        setCommentsState((prevState) => ({ ...prevState, page: 1 }));
        setCommentLoading(false);
        getComments();
      }
      onIdeaActivity(forIdea);
    }, (err) => toast.error(err.message));
  }, [commentText, replyText, setCommentsState, t, forIdea, getComments, forId, onIdeaActivity, setCommentText, setReplyText]);

  const deleteComment = useCallback((commentId: string) => util.confirm(t('ideas.comments.delete.title'), t('ideas.comments.delete.info'))
    .then(() => {
      api.boards.deleteProjectComment(forId, forIdea, commentId, () => {
        const currentComments = Object.assign([], comments).filter((c) => c._id !== commentId);
        setComments(currentComments);
        toast(t('ideas.comments.delete.success'));
      }, (err) => toast.error(err.message));
    }).catch(() => { }), [forIdea, t, comments, forId]);

  return (
    <div style={{ width: '100%' }}>
      <p style={{ color: 'grey' }}>These notes are only visible to admins and users with access to the project board.</p>
      <DiscussionFeed
        post={postComment}
        deleteComment={deleteComment}
        feed={comments}
        loading={commentsLoading}
        commentLoading={commentLoading}
        canPost={true}
        postReply={(parent) => postComment(parent)}
        onPageChange={onPageChange}
        updateComment={(text) => setCommentText(text)}
        updateReply={(commentId, text) => setReplyText((prev) => ({ ...prev, [commentId]: { text, media: [] } }))}
        getReplyData={(commentId) => replyText[commentId] ?? { text: '', media: [] }}
        commentText={commentText}
        replyText={replyText}
        page={page}
        limit={limit}
        nextPageAvailable={nextPageAvailable}
        previousPageAvailable={previousPageAvailable}
        total={total}
        user={user}
        disableReacts
        disableAttachments
        postText='Post note'
        placeholder='Enter a comment..'
        type='projectComment'
      />
    </div>
  );
};

export default ProjectComments;
